import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import {
  fetchPoliciesByAddress,
  FetchPoliciesByAddressRequest,
  fetchPoliciesByPerson,
  FetchPoliciesByPersonRequest,
  fetchPoliciesByPolicyNumber,
  FetchPoliciesByPolicyNumberRequest,
  FetchPoliciesResponse,
} from "../../../api/policy";
import { Button } from "../../../components/buttons/Button";
import { Divider } from "../../../components/dividers/Divider";
import { RadioButtons } from "../../../components/InputFields/radioButtons/RadioButtons";
import { Column, Table } from "../../../components/table/Table";
import { ValidationMessage } from "../../../components/validationMessage/ValidationMessage";
import { sortDate } from "../../../lib/sortUtil";
import { POLICY_NR_QUERY_PARAM } from "../../policyDetails/PolicyDetails";
import { SearchByAddressFields } from "../searchByAddressFields/SearchByAddressFields";
import { SearchByPersonFields } from "../searchByPersonFields/SearchByPersonFields";
import { SearchByPolicyNumberFields } from "../searchByPolicyNumberFields/SearchByPolicyNumberFields";
import styles from "./SearchDupDlpPolicy.module.scss";
import {
  AddressSearchData,
  initialState,
  PersonSearchData,
  PolicyNumberSearchData,
  searchDupDlpPolicyReducer,
  SearchOption,
} from "./SearchDupDlpPolicy.state";

interface FetchPolicyList {
  (
    requestData: Partial<FetchPoliciesByAddressRequest>,
    requestFunction: (request: Partial<FetchPoliciesByAddressRequest>) => Promise<FetchPoliciesResponse>
  ): Promise<void>;
  (
    requestData: Partial<FetchPoliciesByPersonRequest>,
    requestFunction: (request: Partial<FetchPoliciesByPersonRequest>) => Promise<FetchPoliciesResponse>
  ): Promise<void>;
  (
    requestData: Partial<FetchPoliciesByPolicyNumberRequest>,
    requestFunction: (request: Partial<FetchPoliciesByPolicyNumberRequest>) => Promise<FetchPoliciesResponse>
  ): Promise<void>;
}

interface TableRowData {
  policyNumber: string;
  insurance: string;
  role: string;
  name: string;
  dateOfBirth: string;
  address: string;
}

interface Props {
  fields: Record<string, Field<string & { href: string }>>;
  dictionary: Record<string, string>;
}

export const SearchDupDlpPolicy = ({ fields, dictionary }: Props) => {
  const history = useHistory();
  const [
    { data, searchOption, modelState, loading, addressSearchData, personSearchData, policyNumberSearchData },
    dispatch,
  ] = useReducer(searchDupDlpPolicyReducer, initialState);

  const columns: Column<TableRowData>[] = [
    { Header: fields.PolicyLabel.value, accessor: "policyNumber", collapse: true },
    { Header: fields.InsuranceLabel.value, accessor: "insurance", collapse: true },
    { Header: fields.RoleLabel.value, accessor: "role", collapse: true },
    { Header: fields.NameLabel.value, accessor: "name" },
    { Header: fields.DateOfBirthLabel.value, accessor: "dateOfBirth", collapse: true, sortType: sortDate },
    { Header: fields.AddressLabel.value, accessor: "address" },
  ];

  const createNoResultMessage = () => {
    if (data === undefined) {
      return fields.SearchMessage.value;
    } else if (data.length === 0) {
      return `${fields.NoDataFoundMessage.value} \n ${fields.SuggestionMessage.value}`;
    } else {
      return undefined;
    }
  };

  const options: { value: SearchOption; label: string }[] = [
    {
      value: "address",
      label: fields.AddressRadioButton.value,
    },
    {
      value: "person",
      label: fields.PersonRadioButton.value,
    },
    {
      value: "policyNumber",
      label: fields.PolicyNumberRadioButton.value,
    },
  ];

  const fetchPolicyList: FetchPolicyList = async (requestData, requestFunction) => {
    try {
      dispatch({ type: "FETCH_DATA" });

      const policies = await requestFunction({ ...requestData });

      if ("modelState" in policies) {
        dispatch({
          type: "FETCH_ERROR",
          payload: { modelState: { genericMessage: [fields.GenericValidationMessage.value] } },
        });
      } else {
        dispatch({
          type: "FETCH_SUCCESS",
          payload: { data: { policies, dictionary } },
        });
      }
    } catch {
      dispatch({
        type: "FETCH_ERROR",
        payload: { modelState: { genericMessage: [fields.GenericErrorMessage.value] } },
      });
    }
  };

  const handleChangeSearchOption = (value: SearchOption) => {
    dispatch({ type: "UPDATE_SEARCH_OPTION", payload: value });
  };

  const handleUpdateAddress = (address: Partial<AddressSearchData>) => {
    dispatch({ type: "UPDATE_ADDRESS_SEARCH_DATA", payload: address });
  };

  const handleUpdatePerson = (person: Partial<PersonSearchData>) => {
    dispatch({ type: "UPDATE_PERSON_SEARCH_DATA", payload: person });
  };

  const handleUpdatePolicyNumber = (policyNumber: Partial<PolicyNumberSearchData>) => {
    dispatch({ type: "UPDATE_POLICY_NUMBER_SEARCH_DATA", payload: policyNumber });
  };

  const onClickSearch = async () => {
    switch (searchOption) {
      case "address":
        await fetchPolicyList(addressSearchData, fetchPoliciesByAddress);
        break;
      case "person":
        await fetchPolicyList(personSearchData, fetchPoliciesByPerson);
        break;
      case "policyNumber":
        await fetchPolicyList(policyNumberSearchData, fetchPoliciesByPolicyNumber);
    }
  };

  const renderSearchFields = () => {
    switch (searchOption) {
      case "address":
        return (
          <SearchByAddressFields fields={fields} handleUpdateAddress={handleUpdateAddress} values={addressSearchData} />
        );
      case "person":
        return (
          <SearchByPersonFields fields={fields} handleUpdatePerson={handleUpdatePerson} values={personSearchData} />
        );
      case "policyNumber":
        return (
          <SearchByPolicyNumberFields
            fields={fields}
            handleUpdatePolicyNumber={handleUpdatePolicyNumber}
            values={policyNumberSearchData}
          />
        );
    }
  };

  const canClickOnRow = Boolean(fields.DetailPageLink?.value?.href);

  const handleRowClick = ({ policyNumber }: TableRowData) => {
    history.push(`${fields.DetailPageLink.value.href}?${POLICY_NR_QUERY_PARAM}=${policyNumber}`);
  };

  return (
    <>
      <div className={styles.searchDupDlpPolicy__search}>
        <h4>{fields.SearchLabel.value}</h4>

        <RadioButtons
          fieldData={fields}
          name="SearchOptions"
          fieldName="SearchOptions"
          required
          selectedValue={searchOption}
          options={options}
          propertyName="model.SearchOptions"
          onChange={handleChangeSearchOption}
          direction="row"
          disabled={loading}
        />
      </div>
      <div>
        <div className={styles.searchDupDlpPolicy__container}>
          {renderSearchFields()}
          <div className={styles.searchDupDlpPolicy__searchButton}>
            <Button loading={loading} leading={<i className="icon icon-search" />} onClick={onClickSearch}>
              <Text field={fields.SearchButton} />
            </Button>
          </div>
        </div>
        <div className={styles.searchDupDlpPolicy__error}>
          <ValidationMessage id="validationMessageSearchPolicy" modelState={modelState} property="genericMessage" />
        </div>
      </div>
      <Divider type="horizontal" />
      <Table
        columns={columns}
        data={data ?? []}
        noResultsText={createNoResultMessage()}
        onRowClick={canClickOnRow ? handleRowClick : undefined}
      />
    </>
  );
};
